import pluralize from 'pluralize';
import { ReportSourceObject } from '@varos/research-react';
import { SourceListController } from './sourceListInterface';
import { SourceItemProps } from '../item';
import { SourceListViewProps } from './sourceListProps';
import { useMemo } from 'react';

export function createSourceListController(): SourceListController {
    const MAX_SOURCES = 5;
    return {
        useProps(context, item, props): SourceListViewProps {
            const handleOpenSource = (content: ReportSourceObject) => {
                const url = content.url;
                if (url) {
                    window.open(url, '_blank');
                    return;
                }
                return props.onOpen(content);
            };

            const sources = useMemo(
                () =>
                    item.items.flatMap((item): SourceItemProps[] => {
                        const { root: source } = item;
                        return [
                            {
                                id: source.id,
                                label: source.displayName,
                                description: source.description ?? 'no description',
                                image: null,
                                onOpen: handleOpenSource.bind(null, source),
                                score: {
                                    total: {
                                        numeric: 0,
                                        colorScheme: 'red',
                                    },
                                },
                                original: {
                                    type: source.original.type,
                                },
                                references: {
                                    count: item.mentions.length,
                                    label: [
                                        item.mentions.length,
                                        pluralize('reference', item.mentions.length),
                                    ].join(' '),
                                },
                            },
                        ];
                    }),
                [item.items]
            );

            const filteredSources = useMemo(() => {
                return sources.filter((source) =>
                    props.active.value === null
                        ? true
                        : source.original.type === props.active.value
                );
            }, [sources, props.active.value]);

            const truncatedItems = props.expanded.value
                ? filteredSources
                : filteredSources.slice(0, MAX_SOURCES);

            const hasTruncated = sources.length > MAX_SOURCES;
            let expand: SourceListViewProps['expand'] = null;

            if (hasTruncated) {
                expand = {
                    count: sources.length - truncatedItems.length,
                    label: props.expanded.value
                        ? `Show less`
                        : `Show more (${sources.length - truncatedItems.length})`,
                    button: {
                        onClick: props.expanded.onChange.bind(
                            null,
                            !props.expanded.value
                        ),
                    },
                };
            }

            return {
                facets: [
                    {
                        id: 'all',
                        label: 'All',
                        count: item.counts.total,
                        button: {
                            isSelected: props.active.value === null,
                            onClick: props.active.onChange.bind(null, null),
                        },
                    },
                    ...item.facets.map((facet) => ({
                        ...facet,
                        button: {
                            isSelected: facet.id === props.active.value,
                            onClick: props.active.onChange.bind(null, facet.id),
                        },
                    })),
                ],
                items: truncatedItems,
                expand,
            };
        },
    };
}
